"use strict";

import "./Dashboard.css";

import React from "react";

import dispatcher from "./../../dispatcher";
import sessionStore from "./../../store/session";
import dataTypeStore from "./../../store/dataTypes";
import serviceStore from "./../../store/services";
import vehicleStore from "./../../store/vehicles";
import vehicleStateStore from "./../../store/vehicleStates";
import poiStore from "./../../store/pois";
import selectedVehicleStore from "./../../store/selectedVehicle";
import selectedPOIStore from "./../../store/selectedPOI";
import mapStateStore from "./../../store/mapState";

import lang from "vwa/lang";
import wsi from "./../../wsi";
import filterGroups from "vwa/utils/filterGroups";

import getDataTypes from "./../../api/getDataTypes";
import getServices from "./../../api/getServices";
import login from "./../../api/login";
import getVehicles from "./../../api/getVehicles";
import getVehicleStates from "./../../api/getVehicleStates";
import getPOIs from "./../../api/getPOIs";
import getUsers from "./../../api/getUsers";
import getEventDescriptors from "./../../api/getEventDescriptors";
import resolveCarrierState from "./../../api/resolveCarrierState";
	
import tokenProvider from "vwa/token";

import {getBoundsOfPOI} from "./../../utils/poi";

import LoginDialog from "vwa/components/LoginDialog";
import CreatePOIDialog from "./../CreatePOIDialog";
import ConfirmDialog from "vwa/components/ConfirmDialog";

import ProgressBar from "vwa/components/ProgressBar";
import Icon from "vwa/components/Icon";
import Button from "vwa/components/Button";
import Tabs from "vwa/components/Tabs";
import Pager from "vwa/components/Pager";
import {DropdownMenu, Item, Separator} from "vwa/components/DropdownMenu";

import shouldComponentUpdate from "vwa/shouldComponentUpdate";

import VehicleList from "./../VehicleList";
import POIList from "./../POIList";

import RightContainer from "./RightContainer";

import logo from "./only_logo.svg";

const DETAILS_DIVIDING_DENOMINATOR = 7;
const DETAILS_DEFAULT_HEIGHT_RATIO = 4;
const DETAILS_ANIMATION_DURATION = 500; // ms

export default class Dashboard extends React.Component {
	
	constructor(props) {
		super(props);
		this.initState();
		this.setStoreListeners();

		// This binding is necessary to make `this` work in the callback
		this.toggleLeftContainer = this.toggleLeftContainer.bind(this);
	}
	
	initState() {
		this.state = {
			session: sessionStore.get(),
			hasInitData: false,
			hasApplicationData: false,
			progress: lang.GETTING_APPLICATION_DATA,
			page: "vehicles",
			isLeftContainerHidden: false
		};
	}
	
	setStoreListeners() {
		sessionStore.on('change', session => this.handleSessionChange(session));
		dataTypeStore.on('change', _ => this.checkInitData());
		serviceStore.on('change', _ => this.checkInitData());
		vehicleStore.on('change', _ => this.checkApplicationData());
		poiStore.on('change', _ => this.checkApplicationData());
		vehicleStateStore.on('change', _ => this.checkApplicationData());
		vehicleStateStore.on('set', vehicleStates => this.handleVehicleStateInitialization(vehicleStates));
		selectedVehicleStore.on('change', vehicleId => this.shouldSwitchPage(vehicleId, 'vehicles'));
		selectedPOIStore.on('change', poiId => this.shouldSwitchPage(poiId, 'pois'));
	}
	
	shouldComponentUpdate(...nexts){
		return shouldComponentUpdate(this, ...nexts);
	}
	
	handleSessionChange(session) {
		this.setState({session});
		if (!session) {
			this.closeWSIConnection();
		};
	}
	
	checkInitData() {
		this.setState({
			hasInitData: !!(dataTypeStore.getAll() && serviceStore.getAll())
		});
	}
	
	shouldSwitchPage(someSelectedId, page) {
		if (!someSelectedId)
			return;
		this.clearSelections(page);
		this.setState({page});
	}
	
	handlePOICreate(location) {
		if (this.refs.createPOIDialog)
			this.refs.createPOIDialog.show(location);
	}
	
	checkApplicationData() {
		this.setState({
			hasApplicationData: !!(vehicleStore.getAll() && vehicleStateStore.getAll() && poiStore.getAll())
		});
	}
	
	componentDidMount() {
		this.getInitData();
	}
	
	handleTabChange(page) {
		if (page != this.state.page) {
			this.clearSelections(null);
			this.setState({page});
		}
	}
	
	async getInitData() {
		await getDataTypes();
		await getServices();
		
		const storedToken = await tokenProvider.get();
		if (storedToken) {
			this.login(storedToken)
		}
		else {
			this.setState({progress: ""});
		}
	}
	
	async login(...args) {
		
		this.refs.loginDialog.setError("");
		
		this.setState({progress: lang.LOGIN+"..."});
		
		try {
			const session = await login(...args);
			this.refs.loginDialog.setError("");
			this.refs.loginDialog.clearInputs();
			dispatcher.dispatch('login', session.token, session.user);
			await this.getApplicationData();
		} catch (error) {
			if (!args[0]) { // ha tokennel jelentkezünk be
				if (error.id)
					this.refs.loginDialog.setError(error.id == "WRONG_USER_OR_PASSWORD" ? /* error.message */  lang.WRONG_USER_OR_PASSWORD : lang.SOMETHING_WRONG);
				else throw error;
			}
			else throw error;
		} finally {
			this.setState({progress: ""});
		}
	}
	
	async getApplicationData() {
		this.setState({progress: lang.GETTING_APPLICATION_DATA});
		await getVehicles();
		await getPOIs();
		await getUsers();
		await getVehicleStates();
		await getEventDescriptors();
		this.setState({progress: ""});
	}
	
	handleVehicleStateInitialization(vehicleStates) {
		if (vehicleStates) {
			this.listenVehicleStates();
			dispatcher.dispatch('pinVehicles', vehicleStates.map(vs => vs.carrier), true);
		}
	}
	
	listenVehicleStates() {
		
		const vehiclesWithoutGroups = filterGroups(vehicleStore.getAll());
		
		wsi.on("auth", _ => {
			wsi.on('newCarrierState', {
				timeLimit: Date.now(),
				carriers: vehiclesWithoutGroups.map(carrier => carrier._id)
			}, async carrierState => {
				await resolveCarrierState(carrierState);
				dispatcher.dispatch('newCarrierState', carrierState);
			});
		});
		wsi.connect();
	}
	
	closeWSIConnection() {
		wsi.close();
	}
	
	/**
		@param {String} whichType Lehet "vehicles" vagy "pois"
	*/
	
	clearSelections(whichType) {
		if (whichType == "vehicles")
			dispatcher.dispatch('selectPOI', null);
		else if (whichType == "pois")
			dispatcher.dispatch('selectVehicle', null);
		else {
			dispatcher.dispatch('selectVehicle', null);
			dispatcher.dispatch('selectPOI', null);
		}
	}

	/*Toggle X button to hide left container*/
	toggleLeftContainer() {
		var leftContainer = document.getElementById('leftContainer');
		if(!this.state.isLeftContainerHidden){
			leftContainer.style.width = "0px";
		/*	leftContainer.style.display = "none";*/
			this.setState({isLeftContainerHidden: true});
		}else{
			var rightContainer = document.getElementById('');
		/*	leftContainer.style.display = "flex"; */
			leftContainer.style.width = "400px";
			this.setState({isLeftContainerHidden: false});
		}	
	}

	renderHeader() {
		return (
			<header>
				<div>
					<DropdownMenu className="Red">
						<Item onClick={() => this.setState({showSettings: true})}>{lang.SETTINGS}</Item>
						<Separator/>
						<Item onClick={() => dispatcher.dispatch("logout")}>{lang.LOGOUT}</Item>
					</DropdownMenu>
				</div>
				<Tabs className="Light" onChange={page => this.handleTabChange(page)} selected={this.state.page}>
					<li id="vehicles"><Icon>car</Icon></li>
					<li id="pois"><Icon>map-marker-radius</Icon></li>
					<li id="alerts"><Icon>bell</Icon></li>
				</Tabs>
		<span id="toggleLeftContainerBtn" onClick={this.toggleLeftContainer}><Icon >close</Icon></span>
			</header>
		);
	}
	
	renderLeftContainer() {
		
		const header = this.renderHeader();
		
		return (
			<div id="leftContainer">
				{header}
				<Pager active={this.state.page}>
					<div id="vehicles">
						<VehicleList/>
					</div>
					<div id="pois">
						<POIList onCreatePOI={_ => this.handlePOICreate()}/>
					</div>
				</Pager>
			</div>
		);
	}
	
	renderProgressBar() {
		return (
			<ProgressBar
				indeterminate
				label={this.state.progress}
				color="#B02234"
				shown={!!this.state.progress}
			/>
		);
	}
	
	renderRightContainer() {
		return(
			<RightContainer
				id="rightContainer"
				showLeftContainerBtn={!this.state.isLeftContainerHidden}
				showSettings={this.state.showSettings}
				onUserSettingsClose={() => this.setState({showSettings: false})}
				onCreatePOI={location => this.handlePOICreate(location)}
			/>
		);
	}
	
	renderContent() {
		
		const shown = !this.state.progress && this.state.session && this.state.hasInitData && this.state.hasApplicationData;
		const contentClassName = shown ? "" : "Hidden";
		const leftContainer = this.renderLeftContainer();
		
		return (
			<div id="content" className={contentClassName}>
				{leftContainer}
				{this.renderRightContainer()}
			</div>
		);
	}
	
	renderDialogs() {
		return (
			<section>
				<LoginDialog
					ref="loginDialog"
					shown={!this.state.progress && !this.state.session && this.state.hasInitData && !this.state.hasApplicationData}
					errorMsg={this.state.loginError}
					onSubmit={(username, password) => this.login(null, username, password)}
				/>
				<CreatePOIDialog ref="createPOIDialog"/>
			</section>
		);
	}
	
	render() {
		
		const dialogs = this.renderDialogs();
		const progressBar = this.renderProgressBar();
		const content = this.renderContent();
		
		return (
			<div className="Dashboard">
				{dialogs}
				{progressBar}
				{content}
				{this.state.isLeftContainerHidden && 
					<button id="toggleLeftContainerBtn2" onClick={this.toggleLeftContainer}><Icon >chevron-double-right</Icon></button>}
			</div>
		);
	}
	
}

function getDetailsSize() {
	if (!displayedDetailsStore.get())
		return null;
	if (!openedDetailsStore.get(displayedDetailsStore.get().id))
		return null;
	return openedDetailsStore.get(displayedDetailsStore.get().id).size;
}


// WEBPACK FOOTER //
// ./src/view/Dashboard/Dashboard.jsx